import { Modal, message } from "antd";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";

import Button from "metabase/common/components/Button/Button";
import Tooltip from "metabase/common/components/Tooltip";
import { alpha, color } from "metabase/lib/colors";

import {
  type DataCopilotLaunchSource,
  requestDataCopilotLaunchUrl,
  trackDataCopilotLaunch,
} from "./launchDataCopilot";

interface OpenDataCopilotProps {
  source: DataCopilotLaunchSource;
  tooltip?: string;
}

const STOP_ACTIVE_RUN_REQUEST = "data-copilot:request-stop-active-run";
const STOP_ACTIVE_RUN_ACK = "data-copilot:active-run-stopped";
const STOP_ACTIVE_RUN_TIMEOUT_MS = 800;

function getIframeOrigin(iframeUrl: string): string | null {
  if (!iframeUrl || typeof window === "undefined") {
    return null;
  }
  try {
    return new URL(iframeUrl, window.location.origin).origin;
  } catch {
    return null;
  }
}

export function OpenDataCopilot({
  source,
  tooltip = "Ask Data Copilot about this result",
}: OpenDataCopilotProps) {
  const [isLaunching, setIsLaunching] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const stopRequestRef = useRef<{
    requestId: string;
    resolve: () => void;
    timerId: number;
  } | null>(null);

  const launchSource = useMemo(
    () => ({
      ...source,
      sourceUrl:
        source.sourceUrl ||
        (typeof window === "undefined" ? "" : window.location.href),
    }),
    [source],
  );

  const modalTitle = useMemo(() => {
    const sourceTitle = launchSource.sourceTitle?.trim();
    return sourceTitle
      ? t`Data Copilot` + ` - ${sourceTitle}`
      : t`Data Copilot`;
  }, [launchSource.sourceTitle]);

  useEffect(() => {
    const handleStopAck = (event: MessageEvent) => {
      const pending = stopRequestRef.current;
      if (!pending) {
        return;
      }
      const expectedOrigin = getIframeOrigin(iframeUrl);
      if (expectedOrigin && event.origin !== expectedOrigin) {
        return;
      }
      const data = event.data;
      if (
        !data ||
        data.type !== STOP_ACTIVE_RUN_ACK ||
        data.requestId !== pending.requestId
      ) {
        return;
      }
      clearTimeout(pending.timerId);
      pending.resolve();
      stopRequestRef.current = null;
    };

    window.addEventListener("message", handleStopAck);
    return () => {
      window.removeEventListener("message", handleStopAck);
      const pending = stopRequestRef.current;
      if (pending) {
        clearTimeout(pending.timerId);
        pending.resolve();
        stopRequestRef.current = null;
      }
    };
  }, [iframeUrl]);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isLaunching || isClosing) {
      return;
    }
    setIsLaunching(true);
    setIsModalOpen(true);
    setIframeUrl("");
    try {
      const launchUrl = await requestDataCopilotLaunchUrl(launchSource);
      setIframeUrl(launchUrl);
      setIframeKey((currentKey) => currentKey + 1);
      trackDataCopilotLaunch(launchSource);
    } catch (error) {
      setIsModalOpen(false);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to open Data Copilot";
      message.error(errorMessage);
    } finally {
      setIsLaunching(false);
    }
  };

  const requestStopActiveRun = useCallback(async () => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow || !iframeUrl) {
      return;
    }

    const requestId = `stop-active-run-${Date.now()}`;
    const targetOrigin = getIframeOrigin(iframeUrl) ?? "*";

    await new Promise<void>((resolve) => {
      const timerId = window.setTimeout(() => {
        if (stopRequestRef.current?.requestId === requestId) {
          stopRequestRef.current = null;
        }
        resolve();
      }, STOP_ACTIVE_RUN_TIMEOUT_MS);

      stopRequestRef.current = { requestId, resolve, timerId };
      iframeWindow.postMessage(
        {
          type: STOP_ACTIVE_RUN_REQUEST,
          requestId,
        },
        targetOrigin,
      );
    });
  }, [iframeUrl]);

  const closeModal = useCallback(async () => {
    if (isClosing) {
      return;
    }
    setIsClosing(true);
    await requestStopActiveRun();
    setIsModalOpen(false);
    setIframeUrl("");
    setIsClosing(false);
  }, [isClosing, requestStopActiveRun]);

  return (
    <>
      <Tooltip tooltip={tooltip}>
        <Button
          success
          small
          onClick={handleClick}
          icon="insight"
          iconSize={16}
          disabled={isLaunching}
        >
          {t`Ask AI`}
        </Button>
      </Tooltip>
      <Modal
        title={modalTitle}
        centered
        open={isModalOpen}
        maskClosable={false}
        onCancel={() => {
          void closeModal();
        }}
        width="90vw"
        footer={null}
        destroyOnHidden
        styles={{
          body: {
            height: "calc(90vh - 56px)",
            overflow: "hidden",
            padding: "0",
          },
        }}
      >
        <div style={{ height: "100%", position: "relative" }}>
          {!iframeUrl && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: alpha(color("white"), 0.82),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: color("text-medium"),
                fontSize: "14px",
                zIndex: 2,
              }}
            >
              {t`Loading Data Copilot...`}
            </div>
          )}
          {iframeUrl && (
            <iframe
              ref={iframeRef}
              key={iframeKey}
              src={iframeUrl}
              title={modalTitle}
              width="100%"
              style={{ height: "100%", border: "none" }}
              allow="clipboard-read; clipboard-write"
            />
          )}
        </div>
      </Modal>
    </>
  );
}
